// keep byte-parallel with src/lib/olmps/question-banks.ts
/**
 * OLMPS V9 — intake question banks (data only, no React, no DB).
 *
 * Two families, per docs/OLMPS_V9_SPEC.md §3/§9/§13 and docs/OLMPS_SPINE_SPEC.md
 * (question-count rules):
 *
 * 1. SECTOR_BANKS — one per SHAPE sector (Social, Health, Admin, Personal,
 *    Economic). Each holds 20 survey questions (UI asks a minimum of 5, up to
 *    all 20 — more questions = better suggestion) whose answers fold into a
 *    suggested Endeavour ("Having …" form), plus a `presetEndeavours` list
 *    that doubles as the manual-path dropdown suggestions (decision #6:
 *    manual subsumes cascade — there is no separate cascade flow).
 *
 * 2. GEAR_BANKS — one per ORDER gear (Orient, Regulate, Direct, Execute,
 *    Revise), per the MoyRi gear-table intake (OLMPS_SPINE_SPEC.md §3, depths
 *    3/10/20). Each holds 20 questions whose answers lean toward the malady
 *    id(s) most active for that gear — reusing the exact malady ids defined
 *    in `spectrum-tools.ts` (no new malady ids invented).
 *
 * SECTOR_DEITY_MAP is the prescriptive Goal-source catalog (§3 of the V9
 * spec): sector → candidate deity id(s), each with themes used to match an
 * Endeavour's language to the deity most likely to serve it.
 *
 * This module is pure data — importing it has no side effects, and it is
 * portable to React Native the same way `spectrum-tools.ts` is.
 */

import type { Gear, Sector } from '../domain/olmps';

// ---------------------------------------------------------------------------
// Sector surveys
// ---------------------------------------------------------------------------

/** One answer choice on a sector survey question. */
export interface SectorSurveyOption {
  label: string;
  /** Endeavour phrase this answer leans toward, "Having …" form. */
  leansEndeavour: string;
}

/** One sector survey question. 3-4 options, each with an `leansEndeavour` hint. */
export interface SectorSurveyQuestion {
  id: string;
  prompt: string;
  options: SectorSurveyOption[];
}

/** The full survey + preset list for one SHAPE sector. */
export interface SectorBank {
  sector: Sector;
  label: string;
  /**
   * 6-10 "Having …" phrases — the manual-path dropdown suggestions
   * (decision #6: manual subsumes cascade, these are its presets).
   */
  presetEndeavours: string[];
  /** 20 questions; UI slices min 5 / max 20 per the spine spec's count rule. */
  questions: SectorSurveyQuestion[];
}

const sq = (id: string, prompt: string, options: SectorSurveyOption[]): SectorSurveyQuestion => ({
  id,
  prompt,
  options,
});

// --- Social ------------------------------------------------------------

const SOCIAL_PRESETS: string[] = [
  'Having a close circle of trusted friends',
  'Having a supportive family relationship',
  'Having an active dating life or partnership',
  'Having a wide, engaged professional network',
  'Having strong communication skills in conflict',
  'Having a reliable community or group I belong to',
  'Having regular quality time with people I love',
  'Having confidence in social settings',
];

const SOCIAL_QUESTIONS: SectorSurveyQuestion[] = [
  sq('soc-01', 'When a friend cancels plans last minute, you usually…', [
    { label: 'shrug it off — we barely keep in touch anyway', leansEndeavour: 'Having a close circle of trusted friends' },
    { label: 'feel it more than I expect to and wonder if I have anyone reliable', leansEndeavour: 'Having a close circle of trusted friends' },
    { label: 'reschedule without a second thought — we do this often', leansEndeavour: 'Having regular quality time with people I love' },
    { label: 'use the free time to catch up on a hobby or errands', leansEndeavour: 'Having confidence in social settings' },
  ]),
  sq('soc-02', 'A family gathering is coming up. Your gut reaction is…', [
    { label: 'dread — there is unresolved tension I keep avoiding', leansEndeavour: 'Having a supportive family relationship' },
    { label: 'mild indifference, we see each other out of habit', leansEndeavour: 'Having a supportive family relationship' },
    { label: 'genuine excitement, I look forward to it', leansEndeavour: 'Having regular quality time with people I love' },
  ]),
  sq('soc-03', 'When you meet someone new at an event, you…', [
    { label: 'freeze up or stick to the one person I already know', leansEndeavour: 'Having confidence in social settings' },
    { label: 'manage small talk but rarely follow up after', leansEndeavour: 'Having a wide, engaged professional network' },
    { label: 'exchange contact info and actually follow up within the week', leansEndeavour: 'Having a wide, engaged professional network' },
  ]),
  sq('soc-04', 'Your last serious romantic relationship (or lack of one) makes you feel…', [
    { label: "like dating is a low priority I keep putting off", leansEndeavour: 'Having an active dating life or partnership' },
    { label: "like I want it but don't know how to make it happen", leansEndeavour: 'Having an active dating life or partnership' },
    { label: 'satisfied with where things stand', leansEndeavour: 'Having a close circle of trusted friends' },
  ]),
  sq('soc-05', 'In an argument with someone close to you, you tend to…', [
    { label: 'go silent and let it fester for days', leansEndeavour: 'Having strong communication skills in conflict' },
    { label: 'say something sharp I regret later', leansEndeavour: 'Having strong communication skills in conflict' },
    { label: 'stay calm, say my piece, and resolve it the same day', leansEndeavour: 'Having strong communication skills in conflict' },
  ]),
  sq('soc-06', 'Do you have a group (club, team, faith community, hobby crew) you show up for regularly?', [
    { label: 'no, I keep meaning to find one', leansEndeavour: 'Having a reliable community or group I belong to' },
    { label: 'I used to but drifted away', leansEndeavour: 'Having a reliable community or group I belong to' },
    { label: 'yes, and it grounds my week', leansEndeavour: 'Having a reliable community or group I belong to' },
  ]),
  sq('soc-07', 'When was the last time you had an uninterrupted one-on-one conversation with someone you love?', [
    { label: "I honestly can't remember", leansEndeavour: 'Having regular quality time with people I love' },
    { label: 'a couple weeks ago, it happens occasionally', leansEndeavour: 'Having regular quality time with people I love' },
    { label: 'this week, it happens like clockwork', leansEndeavour: 'Having regular quality time with people I love' },
  ]),
  sq('soc-08', 'At a party where you know almost no one, you…', [
    { label: 'leave early or hover near the exit', leansEndeavour: 'Having confidence in social settings' },
    { label: 'stay but wait for others to approach me', leansEndeavour: 'Having confidence in social settings' },
    { label: 'introduce myself around and enjoy it', leansEndeavour: 'Having confidence in social settings' },
  ]),
  sq('soc-09', 'When a coworker or contact could help your career, do you reach out?', [
    { label: 'no, it feels transactional and awkward', leansEndeavour: 'Having a wide, engaged professional network' },
    { label: 'sometimes, if it is low-effort', leansEndeavour: 'Having a wide, engaged professional network' },
    { label: 'yes, I maintain those relationships deliberately', leansEndeavour: 'Having a wide, engaged professional network' },
  ]),
  sq('soc-10', 'How many people could you call at 2am if something went wrong?', [
    { label: 'none I would feel right calling', leansEndeavour: 'Having a close circle of trusted friends' },
    { label: 'one, maybe two', leansEndeavour: 'Having a close circle of trusted friends' },
    { label: 'several, without hesitation', leansEndeavour: 'Having a close circle of trusted friends' },
  ]),
  sq('soc-11', 'When your partner (or a close friend) brings up something that hurt them, you…', [
    { label: 'get defensive before I have heard them out', leansEndeavour: 'Having strong communication skills in conflict' },
    { label: 'listen but struggle to respond well in the moment', leansEndeavour: 'Having strong communication skills in conflict' },
    { label: 'listen fully and reflect it back before responding', leansEndeavour: 'Having strong communication skills in conflict' },
  ]),
  sq('soc-12', 'How often do you initiate plans rather than wait to be invited?', [
    { label: 'almost never', leansEndeavour: 'Having a close circle of trusted friends' },
    { label: 'sometimes, when I think of it', leansEndeavour: 'Having regular quality time with people I love' },
    { label: 'regularly — I am usually the one organizing', leansEndeavour: 'Having a reliable community or group I belong to' },
  ]),
  sq('soc-13', 'Family holidays or milestones — do you make the effort to attend and engage?', [
    { label: 'I skip when I can find an excuse', leansEndeavour: 'Having a supportive family relationship' },
    { label: 'I attend but check out mentally', leansEndeavour: 'Having a supportive family relationship' },
    { label: "I show up and I'm genuinely present", leansEndeavour: 'Having regular quality time with people I love' },
  ]),
  sq('soc-14', 'When you picture your ideal week socially, what is missing right now?', [
    { label: 'romantic connection', leansEndeavour: 'Having an active dating life or partnership' },
    { label: 'a sense of belonging to something bigger than me', leansEndeavour: 'Having a reliable community or group I belong to' },
    { label: 'nothing — my social life feels full already', leansEndeavour: 'Having a close circle of trusted friends' },
  ]),
  sq('soc-15', 'How do you feel walking into a room where you have to introduce yourself?', [
    { label: 'anxious, I rehearse what to say', leansEndeavour: 'Having confidence in social settings' },
    { label: 'neutral, I get through it fine', leansEndeavour: 'Having confidence in social settings' },
    { label: 'comfortable, I enjoy meeting new people', leansEndeavour: 'Having a wide, engaged professional network' },
  ]),
  sq('soc-16', 'When a friendship goes quiet for months, do you reach back out?', [
    { label: 'no, I assume it has run its course', leansEndeavour: 'Having a close circle of trusted friends' },
    { label: 'eventually, if it crosses my mind at the right time', leansEndeavour: 'Having a close circle of trusted friends' },
    { label: 'yes, I make the first move', leansEndeavour: 'Having regular quality time with people I love' },
  ]),
  sq('soc-17', "Do you know your family members' current struggles, not just the headline news?", [
    { label: 'not really, we keep things surface-level', leansEndeavour: 'Having a supportive family relationship' },
    { label: 'some of it', leansEndeavour: 'Having a supportive family relationship' },
    { label: 'yes, we talk about the real stuff', leansEndeavour: 'Having a supportive family relationship' },
  ]),
  sq('soc-18', 'If you wanted a date this week, how would you go about it?', [
    { label: "I wouldn't — dating feels like too much friction right now", leansEndeavour: 'Having an active dating life or partnership' },
    { label: 'I would think about it but probably not act', leansEndeavour: 'Having an active dating life or partnership' },
    { label: "I'd message someone or use an app the same day", leansEndeavour: 'Having an active dating life or partnership' },
  ]),
  sq('soc-19', 'When your group chat or crew makes plans, are you usually included and do you show up?', [
    { label: "I've drifted out of the loop", leansEndeavour: 'Having a reliable community or group I belong to' },
    { label: "I'm included but skip more than I attend", leansEndeavour: 'Having a reliable community or group I belong to' },
    { label: "I'm in and I show up consistently", leansEndeavour: 'Having a reliable community or group I belong to' },
  ]),
  sq('soc-20', 'After a disagreement, how long does the tension usually linger?', [
    { label: 'days, sometimes longer, unspoken', leansEndeavour: 'Having strong communication skills in conflict' },
    { label: 'a day or so', leansEndeavour: 'Having strong communication skills in conflict' },
    { label: "it clears the same day, we've talked it through", leansEndeavour: 'Having strong communication skills in conflict' },
  ]),
];

// --- Health --------------------------------------------------------------

const HEALTH_PRESETS: string[] = [
  'Having a healthy energetic body',
  'Having consistent restorative sleep',
  'Having a strong recovery system after injury or illness',
  'Having a nutritious, sustainable diet',
  'Having a regular movement or exercise practice',
  'Having low chronic stress in the body',
  'Having a healthy weight I maintain without a fight',
  'Having freedom from a recurring health issue',
];

const HEALTH_QUESTIONS: SectorSurveyQuestion[] = [
  sq('hea-01', 'On a normal weeknight, what time do you actually fall asleep versus what you planned?', [
    { label: 'an hour or more later, most nights', leansEndeavour: 'Having consistent restorative sleep' },
    { label: 'roughly on time, give or take', leansEndeavour: 'Having consistent restorative sleep' },
    { label: 'right on schedule — it is a fixed ritual', leansEndeavour: 'Having consistent restorative sleep' },
  ]),
  sq('hea-02', 'When you wake up, how do you feel before coffee or any stimulant?', [
    { label: 'foggy and dragging', leansEndeavour: 'Having consistent restorative sleep' },
    { label: 'okay, functional', leansEndeavour: 'Having a healthy energetic body' },
    { label: 'clear and ready to move', leansEndeavour: 'Having a healthy energetic body' },
  ]),
  sq('hea-03', 'How many meals this week were planned versus grabbed out of necessity?', [
    { label: 'almost all grabbed, whatever was around', leansEndeavour: 'Having a nutritious, sustainable diet' },
    { label: 'a mix', leansEndeavour: 'Having a nutritious, sustainable diet' },
    { label: 'mostly planned, I know what I am eating and why', leansEndeavour: 'Having a nutritious, sustainable diet' },
  ]),
  sq('hea-04', 'When did you last do any deliberate physical exercise (not just incidental walking)?', [
    { label: "I can't recall, it has been a while", leansEndeavour: 'Having a regular movement or exercise practice' },
    { label: 'within the past week or two, inconsistently', leansEndeavour: 'Having a regular movement or exercise practice' },
    { label: 'a scheduled session most days', leansEndeavour: 'Having a regular movement or exercise practice' },
  ]),
  sq('hea-05', 'Climbing a few flights of stairs, how does your body respond?', [
    { label: 'winded and heavy, it takes a minute to recover', leansEndeavour: 'Having a healthy energetic body' },
    { label: 'fine, a bit of effort', leansEndeavour: 'Having a healthy energetic body' },
    { label: 'no issue at all', leansEndeavour: 'Having a regular movement or exercise practice' },
  ]),
  sq('hea-06', 'When you get sick or injured, how long does it typically take you to bounce back compared to what feels normal for your age?', [
    { label: 'noticeably longer, recovery drags on', leansEndeavour: 'Having a strong recovery system after injury or illness' },
    { label: 'about what I would expect', leansEndeavour: 'Having a strong recovery system after injury or illness' },
    { label: 'quicker than most people I know', leansEndeavour: 'Having a strong recovery system after injury or illness' },
  ]),
  sq('hea-07', 'Do you have a nagging health issue (back pain, digestion, headaches, etc.) that keeps returning?', [
    { label: 'yes, and I have mostly stopped trying to fix it', leansEndeavour: 'Having freedom from a recurring health issue' },
    { label: 'yes, but I am actively managing it', leansEndeavour: 'Having freedom from a recurring health issue' },
    { label: 'no, nothing chronic right now', leansEndeavour: 'Having a healthy energetic body' },
  ]),
  sq('hea-08', 'Under a stressful week, what happens in your body?', [
    { label: 'tension headaches, tight shoulders, or gut trouble show up', leansEndeavour: 'Having low chronic stress in the body' },
    { label: 'some tightness but it passes', leansEndeavour: 'Having low chronic stress in the body' },
    { label: 'I have practices that keep it from accumulating', leansEndeavour: 'Having low chronic stress in the body' },
  ]),
  sq('hea-09', 'How does your current weight compare to where you feel best, and how much effort does maintaining it take?', [
    { label: "it's a constant fight I'm losing", leansEndeavour: 'Having a healthy weight I maintain without a fight' },
    { label: 'close, with some ongoing effort', leansEndeavour: 'Having a healthy weight I maintain without a fight' },
    { label: 'it holds steady without much thought', leansEndeavour: 'Having a healthy weight I maintain without a fight' },
  ]),
  sq('hea-10', 'When you cook or order food, do you check what is actually in it (sugar, portions, ingredients)?', [
    { label: 'rarely, I just eat what looks good', leansEndeavour: 'Having a nutritious, sustainable diet' },
    { label: 'sometimes, for bigger meals', leansEndeavour: 'Having a nutritious, sustainable diet' },
    { label: 'yes, it is second nature by now', leansEndeavour: 'Having a nutritious, sustainable diet' },
  ]),
  sq('hea-11', 'How many hours of sleep do you average, and does it match what your body needs?', [
    { label: 'well under what I need, most nights', leansEndeavour: 'Having consistent restorative sleep' },
    { label: 'close but a bit short', leansEndeavour: 'Having consistent restorative sleep' },
    { label: 'a full, consistent amount', leansEndeavour: 'Having consistent restorative sleep' },
  ]),
  sq('hea-12', 'After a full day of physical or mental exertion, how do you feel the next morning?', [
    { label: 'wrecked, it takes days to feel normal', leansEndeavour: 'Having a strong recovery system after injury or illness' },
    { label: 'tired but functional', leansEndeavour: 'Having low chronic stress in the body' },
    { label: 'refreshed, my recovery is quick', leansEndeavour: 'Having a strong recovery system after injury or illness' },
  ]),
  sq('hea-13', 'Do you have a consistent exercise routine you follow week to week, or does it depend on motivation?', [
    { label: 'entirely dependent on motivation, which is unreliable', leansEndeavour: 'Having a regular movement or exercise practice' },
    { label: 'a loose routine I mostly stick to', leansEndeavour: 'Having a regular movement or exercise practice' },
    { label: 'a fixed schedule I follow regardless of mood', leansEndeavour: 'Having a regular movement or exercise practice' },
  ]),
  sq('hea-14', 'When stress spikes, do you reach for food, alcohol, or other things to cope?', [
    { label: 'yes, often, and I notice the pattern', leansEndeavour: 'Having low chronic stress in the body' },
    { label: 'occasionally', leansEndeavour: 'Having a nutritious, sustainable diet' },
    { label: 'no, I have other outlets that work', leansEndeavour: 'Having low chronic stress in the body' },
  ]),
  sq('hea-15', 'Think of your energy across a full day (not just morning) — where does it usually crash?', [
    { label: 'multiple crashes, hard to predict', leansEndeavour: 'Having a healthy energetic body' },
    { label: 'one predictable afternoon dip', leansEndeavour: 'Having a healthy energetic body' },
    { label: 'it stays fairly level all day', leansEndeavour: 'Having a healthy energetic body' },
  ]),
  sq('hea-16', 'After a minor injury (sprain, strain, cut), do you follow through on the recovery steps (rest, stretching, physio) or push through?', [
    { label: 'I push through and it comes back worse later', leansEndeavour: 'Having a strong recovery system after injury or illness' },
    { label: 'I rest some but skip the follow-through', leansEndeavour: 'Having a strong recovery system after injury or illness' },
    { label: 'I follow the full recovery protocol', leansEndeavour: 'Having a strong recovery system after injury or illness' },
  ]),
  sq('hea-17', 'How much of your diet is decided in advance versus reactive to hunger and convenience?', [
    { label: 'almost entirely reactive', leansEndeavour: 'Having a nutritious, sustainable diet' },
    { label: 'about half planned', leansEndeavour: 'Having a nutritious, sustainable diet' },
    { label: 'mostly planned ahead', leansEndeavour: 'Having a healthy weight I maintain without a fight' },
  ]),
  sq('hea-18', 'Do you have a wind-down routine before bed, or does sleep just happen whenever?', [
    { label: 'no routine — I fall into bed whenever screens stop', leansEndeavour: 'Having consistent restorative sleep' },
    { label: 'a loose routine some nights', leansEndeavour: 'Having consistent restorative sleep' },
    { label: 'a fixed wind-down I follow nightly', leansEndeavour: 'Having consistent restorative sleep' },
  ]),
  sq('hea-19', 'When you notice tension building in your body during the day, do you do anything about it in the moment?', [
    { label: 'no, I ignore it until it forces me to stop', leansEndeavour: 'Having low chronic stress in the body' },
    { label: 'sometimes I take a break', leansEndeavour: 'Having low chronic stress in the body' },
    { label: 'yes, I have a go-to reset (breathing, stretch, walk)', leansEndeavour: 'Having low chronic stress in the body' },
  ]),
  sq('hea-20', 'If nothing changed, where would your body be in five years?', [
    { label: 'worse off — the current path is not sustainable', leansEndeavour: 'Having a healthy energetic body' },
    { label: 'about the same', leansEndeavour: 'Having a healthy weight I maintain without a fight' },
    { label: 'better, I am already trending the right direction', leansEndeavour: 'Having a regular movement or exercise practice' },
  ]),
];

// --- Admin ---------------------------------------------------------------

const ADMIN_PRESETS: string[] = [
  'Having an inbox and paperwork under control',
  'Having all my documents organized and findable',
  'Having bills and admin tasks paid on time, every time',
  'Having a clean, organized living space',
  'Having my digital files and accounts organized',
  'Having a simple, low-friction admin system',
  'Having no backlog of unresolved admin tasks',
  'Having compliance and deadlines tracked with zero misses',
];

const ADMIN_QUESTIONS: SectorSurveyQuestion[] = [
  sq('adm-01', 'When you open your email inbox right now, what do you see?', [
    { label: 'hundreds of unread messages, some important buried in there', leansEndeavour: 'Having an inbox and paperwork under control' },
    { label: 'a manageable number, mostly triaged', leansEndeavour: 'Having an inbox and paperwork under control' },
    { label: 'near zero, everything is actioned or filed', leansEndeavour: 'Having a simple, low-friction admin system' },
  ]),
  sq('adm-02', 'Could you find your passport, lease, or insurance policy right now without searching for more than a minute?', [
    { label: 'no idea where it is', leansEndeavour: 'Having all my documents organized and findable' },
    { label: "I'd have to dig a bit", leansEndeavour: 'Having all my documents organized and findable' },
    { label: 'yes, it has a fixed place', leansEndeavour: 'Having all my documents organized and findable' },
  ]),
  sq('adm-03', 'Have you paid a late fee or missed a bill in the last three months?', [
    { label: 'yes, more than once', leansEndeavour: 'Having bills and admin tasks paid on time, every time' },
    { label: 'once, barely', leansEndeavour: 'Having bills and admin tasks paid on time, every time' },
    { label: 'no, everything is automated or on schedule', leansEndeavour: 'Having a simple, low-friction admin system' },
  ]),
  sq('adm-04', 'Look around the room you are in — is it in a state you would want a guest to see unannounced?', [
    { label: 'no, it would need real cleanup first', leansEndeavour: 'Having a clean, organized living space' },
    { label: 'mostly, a few piles here and there', leansEndeavour: 'Having a clean, organized living space' },
    { label: 'yes, always guest-ready', leansEndeavour: 'Having a clean, organized living space' },
  ]),
  sq('adm-05', 'How many unresolved "I really need to deal with that" admin items are you carrying right now?', [
    { label: 'a long backlog I keep avoiding', leansEndeavour: 'Having no backlog of unresolved admin tasks' },
    { label: 'a couple of small ones', leansEndeavour: 'Having no backlog of unresolved admin tasks' },
    { label: 'none — I close things out as they arrive', leansEndeavour: 'Having a simple, low-friction admin system' },
  ]),
  sq('adm-06', 'When a form or piece of paperwork needs filling out, what happens?', [
    { label: 'it sits for weeks before I get to it', leansEndeavour: 'Having no backlog of unresolved admin tasks' },
    { label: 'I get to it eventually, close to the deadline', leansEndeavour: 'Having compliance and deadlines tracked with zero misses' },
    { label: 'I handle it the day it arrives', leansEndeavour: 'Having a simple, low-friction admin system' },
  ]),
  sq('adm-07', 'Do you know, off the top of your head, when your next tax/license/subscription renewal deadline is?', [
    { label: 'no idea', leansEndeavour: 'Having compliance and deadlines tracked with zero misses' },
    { label: 'roughly', leansEndeavour: 'Having compliance and deadlines tracked with zero misses' },
    { label: 'yes, it is on a calendar with a reminder', leansEndeavour: 'Having compliance and deadlines tracked with zero misses' },
  ]),
  sq('adm-08', 'Your computer desktop or downloads folder right now is…', [
    { label: 'a chaotic dump I avoid looking at', leansEndeavour: 'Having my digital files and accounts organized' },
    { label: 'cluttered but I can find things eventually', leansEndeavour: 'Having my digital files and accounts organized' },
    { label: 'clean, everything has a folder', leansEndeavour: 'Having my digital files and accounts organized' },
  ]),
  sq('adm-09', 'Could you list every subscription and recurring account you pay for, right now, accurately?', [
    { label: 'no, there are probably ones I forgot about', leansEndeavour: 'Having my digital files and accounts organized' },
    { label: 'most of them', leansEndeavour: 'Having my digital files and accounts organized' },
    { label: 'yes, I keep a running list', leansEndeavour: 'Having a simple, low-friction admin system' },
  ]),
  sq('adm-10', 'How does a new admin task (a form, a call to make, a thing to file) usually enter your system?', [
    { label: "it doesn't — it floats in my head until I forget it", leansEndeavour: 'Having a simple, low-friction admin system' },
    { label: 'a sticky note or scattered app', leansEndeavour: 'Having a simple, low-friction admin system' },
    { label: 'one dedicated list or inbox, every time', leansEndeavour: 'Having a simple, low-friction admin system' },
  ]),
  sq('adm-11', 'Is your closet, pantry, or storage area organized enough that you know what you have without checking?', [
    { label: 'no, I rebuy things I already own', leansEndeavour: 'Having a clean, organized living space' },
    { label: 'somewhat', leansEndeavour: 'Having a clean, organized living space' },
    { label: 'yes, everything has a place', leansEndeavour: 'Having a clean, organized living space' },
  ]),
  sq('adm-12', 'When mail or a physical document arrives, what is the very next thing that happens to it?', [
    { label: 'it lands in a pile with everything else', leansEndeavour: 'Having an inbox and paperwork under control' },
    { label: 'I glance at it, then it joins a pile', leansEndeavour: 'Having an inbox and paperwork under control' },
    { label: 'it gets filed, actioned, or shredded immediately', leansEndeavour: 'Having all my documents organized and findable' },
  ]),
  sq('adm-13', 'Do you use auto-pay / calendar reminders for recurring obligations, or rely on memory?', [
    { label: 'memory, and things slip', leansEndeavour: 'Having bills and admin tasks paid on time, every time' },
    { label: 'a mix', leansEndeavour: 'Having bills and admin tasks paid on time, every time' },
    { label: 'everything recurring is automated', leansEndeavour: 'Having a simple, low-friction admin system' },
  ]),
  sq('adm-14', 'Think of the last time you needed an old document (receipt, contract, ID). How long did it take to find it?', [
    { label: 'a long, frustrating search or I never found it', leansEndeavour: 'Having all my documents organized and findable' },
    { label: 'a few minutes', leansEndeavour: 'Having all my documents organized and findable' },
    { label: 'seconds — I know exactly where it lives', leansEndeavour: 'Having my digital files and accounts organized' },
  ]),
  sq('adm-15', 'Does thinking about your admin/paperwork situation cause you low-grade stress?', [
    { label: 'yes, it is a constant background hum', leansEndeavour: 'Having an inbox and paperwork under control' },
    { label: 'a little, sometimes', leansEndeavour: 'Having no backlog of unresolved admin tasks' },
    { label: 'no, it feels handled', leansEndeavour: 'Having a simple, low-friction admin system' },
  ]),
  sq('adm-16', 'When you moved, changed jobs, or had a major life event, how long did the paperwork cleanup take?', [
    { label: "it's still not fully done", leansEndeavour: 'Having no backlog of unresolved admin tasks' },
    { label: 'a few weeks', leansEndeavour: 'Having compliance and deadlines tracked with zero misses' },
    { label: 'it was closed out quickly', leansEndeavour: 'Having a simple, low-friction admin system' },
  ]),
  sq('adm-17', 'Do you have a password manager or account-organization system, or is it scattered across memory/browsers?', [
    { label: 'scattered, I reset passwords constantly', leansEndeavour: 'Having my digital files and accounts organized' },
    { label: 'partially organized', leansEndeavour: 'Having my digital files and accounts organized' },
    { label: 'fully organized in one system', leansEndeavour: 'Having a simple, low-friction admin system' },
  ]),
  sq('adm-18', 'How often does a deadline (renewal, filing, appointment) catch you by surprise?', [
    { label: 'regularly', leansEndeavour: 'Having compliance and deadlines tracked with zero misses' },
    { label: 'occasionally', leansEndeavour: 'Having compliance and deadlines tracked with zero misses' },
    { label: 'almost never — I track them ahead of time', leansEndeavour: 'Having compliance and deadlines tracked with zero misses' },
  ]),
  sq('adm-19', 'If you had to clean your living space for an inspection tomorrow, how much work would it take?', [
    { label: 'a full day or more', leansEndeavour: 'Having a clean, organized living space' },
    { label: 'an hour or two of touch-up', leansEndeavour: 'Having a clean, organized living space' },
    { label: 'almost nothing — it stays that way', leansEndeavour: 'Having a clean, organized living space' },
  ]),
  sq('adm-20', 'When you finish an admin task, does it stay done, or does the same issue reappear later?', [
    { label: 'it usually reappears because I patched it, not fixed it', leansEndeavour: 'Having no backlog of unresolved admin tasks' },
    { label: 'mostly stays done', leansEndeavour: 'Having a simple, low-friction admin system' },
    { label: 'it stays done, permanently resolved', leansEndeavour: 'Having a simple, low-friction admin system' },
  ]),
];

// --- Personal --------------------------------------------------------------

const PERSONAL_PRESETS: string[] = [
  'Having a strong sense of who I am',
  'Having a regular creative or expressive practice',
  'Having inner calm and emotional regulation',
  'Having a meaningful personal growth practice',
  'Having time carved out just for me',
  'Having clarity on what I actually want from life',
  'Having a spiritual or reflective practice',
  'Having self-respect and boundaries I hold',
];

const PERSONAL_QUESTIONS: SectorSurveyQuestion[] = [
  sq('per-01', 'When someone asks "what do you do for yourself, just for you?", what comes to mind?', [
    { label: "nothing comes to mind, I can't remember the last time", leansEndeavour: 'Having time carved out just for me' },
    { label: 'something, but it happens rarely', leansEndeavour: 'Having time carved out just for me' },
    { label: 'a clear, protected block I keep every week', leansEndeavour: 'Having time carved out just for me' },
  ]),
  sq('per-02', 'When someone asks you to describe your values in one breath, can you?', [
    { label: 'no, I would stumble through it', leansEndeavour: 'Having a strong sense of who I am' },
    { label: 'roughly, in general terms', leansEndeavour: 'Having a strong sense of who I am' },
    { label: 'yes, easily and specifically', leansEndeavour: 'Having clarity on what I actually want from life' },
  ]),
  sq('per-03', 'How often do you make something (write, draw, build, play music) purely for its own sake?', [
    { label: 'never, there is no time or habit for it', leansEndeavour: 'Having a regular creative or expressive practice' },
    { label: 'occasionally, in bursts', leansEndeavour: 'Having a regular creative or expressive practice' },
    { label: 'on a regular cadence', leansEndeavour: 'Having a regular creative or expressive practice' },
  ]),
  sq('per-04', 'When a strong emotion (anger, anxiety, sadness) shows up, what happens next?', [
    { label: 'it takes over and drives my behavior for a while', leansEndeavour: 'Having inner calm and emotional regulation' },
    { label: 'I notice it but struggle to steady myself', leansEndeavour: 'Having inner calm and emotional regulation' },
    { label: 'I notice it, name it, and it passes without derailing me', leansEndeavour: 'Having inner calm and emotional regulation' },
  ]),
  sq('per-05', 'Do you say yes to things you actually want to say no to?', [
    { label: 'often, and I resent it afterward', leansEndeavour: 'Having self-respect and boundaries I hold' },
    { label: 'sometimes', leansEndeavour: 'Having self-respect and boundaries I hold' },
    { label: 'rarely — I hold my line', leansEndeavour: 'Having self-respect and boundaries I hold' },
  ]),
  sq('per-06', 'Do you have a book, course, or skill you are deliberately working through right now, just to grow?', [
    { label: 'no, nothing active', leansEndeavour: 'Having a meaningful personal growth practice' },
    { label: 'something I dip in and out of', leansEndeavour: 'Having a meaningful personal growth practice' },
    { label: 'yes, with real progress I can point to', leansEndeavour: 'Having a meaningful personal growth practice' },
  ]),
  sq('per-07', 'If you had a completely free year and unlimited money, would you know what you would actually do with it?', [
    { label: "no, I'd have to think hard about it", leansEndeavour: 'Having clarity on what I actually want from life' },
    { label: 'roughly, some ideas', leansEndeavour: 'Having clarity on what I actually want from life' },
    { label: 'yes, immediately, in detail', leansEndeavour: 'Having clarity on what I actually want from life' },
  ]),
  sq('per-08', 'Do you have any regular practice of stillness, prayer, meditation, or reflection?', [
    { label: 'no, it feels like it would not stick', leansEndeavour: 'Having a spiritual or reflective practice' },
    { label: 'occasionally, when things get hard', leansEndeavour: 'Having a spiritual or reflective practice' },
    { label: 'yes, on a regular schedule', leansEndeavour: 'Having a spiritual or reflective practice' },
  ]),
  sq('per-09', 'When you are alone with no phone and nothing to do, how does it feel?', [
    { label: 'uncomfortable, I reach for a distraction fast', leansEndeavour: 'Having inner calm and emotional regulation' },
    { label: 'okay after a few minutes', leansEndeavour: 'Having a strong sense of who I am' },
    { label: 'genuinely restful — I like my own company', leansEndeavour: 'Having a strong sense of who I am' },
  ]),
  sq('per-10', 'When someone criticizes you unfairly, how long does it stay with you?', [
    { label: 'days, I replay it repeatedly', leansEndeavour: 'Having inner calm and emotional regulation' },
    { label: 'a while, then it fades', leansEndeavour: 'Having self-respect and boundaries I hold' },
    { label: "it doesn't stick — I know who I am regardless", leansEndeavour: 'Having a strong sense of who I am' },
  ]),
  sq('per-11', 'Do you have a hobby that is purely yours, not tied to work or family obligations?', [
    { label: 'no, everything I do serves someone else', leansEndeavour: 'Having time carved out just for me' },
    { label: 'something small, occasionally', leansEndeavour: 'Having a regular creative or expressive practice' },
    { label: 'yes, and I protect the time for it', leansEndeavour: 'Having time carved out just for me' },
  ]),
  sq('per-12', 'When a family member or partner asks for "just one more favor" during your own time, what do you do?', [
    { label: 'always give in, even at real cost to myself', leansEndeavour: 'Having self-respect and boundaries I hold' },
    { label: 'usually give in but feel torn', leansEndeavour: 'Having self-respect and boundaries I hold' },
    { label: 'weigh it honestly and say no when I need to', leansEndeavour: 'Having self-respect and boundaries I hold' },
  ]),
  sq('per-13', 'Looking back a year, can you point to a specific way you have grown as a person?', [
    { label: "not really, it feels like I'm the same", leansEndeavour: 'Having a meaningful personal growth practice' },
    { label: 'somewhat, in small ways', leansEndeavour: 'Having a meaningful personal growth practice' },
    { label: 'yes, clearly and specifically', leansEndeavour: 'Having a meaningful personal growth practice' },
  ]),
  sq('per-14', 'When life gets chaotic, do you have a way to return to a sense of who you are underneath it?', [
    { label: 'no, I lose myself in the chaos', leansEndeavour: 'Having a strong sense of who I am' },
    { label: 'somewhat, it takes effort', leansEndeavour: 'Having inner calm and emotional regulation' },
    { label: 'yes, I have an anchor I return to', leansEndeavour: 'Having a spiritual or reflective practice' },
  ]),
  sq('per-15', 'Do you know what "enough" looks like for you (money, achievement, recognition), or is it always further out?', [
    { label: 'always further out, never enough', leansEndeavour: 'Having clarity on what I actually want from life' },
    { label: 'somewhat defined', leansEndeavour: 'Having clarity on what I actually want from life' },
    { label: 'clearly defined and I recognize when I have it', leansEndeavour: 'Having clarity on what I actually want from life' },
  ]),
  sq('per-16', 'When was the last time you made something and showed it to someone, just because you were proud of it?', [
    { label: "can't recall", leansEndeavour: 'Having a regular creative or expressive practice' },
    { label: 'months ago', leansEndeavour: 'Having a regular creative or expressive practice' },
    { label: 'recently', leansEndeavour: 'Having a regular creative or expressive practice' },
  ]),
  sq('per-17', 'When someone pushes past a boundary you set, what happens?', [
    { label: 'I let it slide to avoid conflict', leansEndeavour: 'Having self-respect and boundaries I hold' },
    { label: 'I address it eventually', leansEndeavour: 'Having self-respect and boundaries I hold' },
    { label: 'I restate it clearly and hold it', leansEndeavour: 'Having self-respect and boundaries I hold' },
  ]),
  sq('per-18', 'Do you set aside any time for questions bigger than the day-to-day (meaning, mortality, purpose)?', [
    { label: 'no, there is no room for that', leansEndeavour: 'Having a spiritual or reflective practice' },
    { label: 'occasionally, usually triggered by a crisis', leansEndeavour: 'Having a spiritual or reflective practice' },
    { label: 'yes, it is a regular part of my week', leansEndeavour: 'Having a spiritual or reflective practice' },
  ]),
  sq('per-19', 'If a stranger described your personality, would it match how you see yourself?', [
    { label: "probably not — I'm not sure who I really am to others", leansEndeavour: 'Having a strong sense of who I am' },
    { label: 'roughly', leansEndeavour: 'Having a strong sense of who I am' },
    { label: 'yes, I present as who I actually am', leansEndeavour: 'Having a strong sense of who I am' },
  ]),
  sq('per-20', 'When you imagine your life five years from now, is the picture something you chose or something that just happened to you?', [
    { label: "it's whatever happens — I haven't chosen it", leansEndeavour: 'Having clarity on what I actually want from life' },
    { label: 'a mix of both', leansEndeavour: 'Having a meaningful personal growth practice' },
    { label: 'something I have actively chosen and am building toward', leansEndeavour: 'Having clarity on what I actually want from life' },
  ]),
];

// --- Economic --------------------------------------------------------------

const ECONOMIC_PRESETS: string[] = [
  'Having consistent, growing income',
  'Having a solid emergency fund',
  'Having debt paid down or eliminated',
  'Having a clear investment or growth plan',
  'Having spending aligned with my values',
  'Having multiple income streams',
  'Having financial independence on a real timeline',
  'Having a budget I actually follow',
];

const ECONOMIC_QUESTIONS: SectorSurveyQuestion[] = [
  sq('eco-01', 'If your income stopped tomorrow, how many months could you cover expenses?', [
    { label: 'less than one', leansEndeavour: 'Having a solid emergency fund' },
    { label: 'one to three', leansEndeavour: 'Having a solid emergency fund' },
    { label: 'six or more', leansEndeavour: 'Having a solid emergency fund' },
  ]),
  sq('eco-02', 'Do you know exactly what you owe across all debts right now, without checking?', [
    { label: "no, and it's a bit scary to add up", leansEndeavour: 'Having debt paid down or eliminated' },
    { label: 'roughly', leansEndeavour: 'Having debt paid down or eliminated' },
    { label: "yes, and it's on a payoff plan", leansEndeavour: 'Having debt paid down or eliminated' },
  ]),
  sq('eco-03', 'At the end of the month, do you know where your money actually went?', [
    { label: 'no, it just disappears', leansEndeavour: 'Having a budget I actually follow' },
    { label: 'roughly, in broad categories', leansEndeavour: 'Having a budget I actually follow' },
    { label: 'yes, tracked line by line', leansEndeavour: 'Having a budget I actually follow' },
  ]),
  sq('eco-04', 'Has your income grown, stayed flat, or shrunk over the past two years?', [
    { label: 'flat or shrunk', leansEndeavour: 'Having consistent, growing income' },
    { label: 'grown a little', leansEndeavour: 'Having consistent, growing income' },
    { label: 'grown meaningfully, and I have a plan for the next raise/deal', leansEndeavour: 'Having consistent, growing income' },
  ]),
  sq('eco-05', 'Do you have money invested (retirement, stocks, real estate) beyond a checking/savings account?', [
    { label: 'no, it all just sits in checking', leansEndeavour: 'Having a clear investment or growth plan' },
    { label: 'some, without a real strategy', leansEndeavour: 'Having a clear investment or growth plan' },
    { label: 'yes, with a plan I revisit regularly', leansEndeavour: 'Having a clear investment or growth plan' },
  ]),
  sq('eco-06', 'When you buy something, is it because you decided it mattered, or because it was convenient/emotional in the moment?', [
    { label: 'mostly impulse or convenience', leansEndeavour: 'Having spending aligned with my values' },
    { label: 'a mix', leansEndeavour: 'Having spending aligned with my values' },
    { label: 'almost always a deliberate choice', leansEndeavour: 'Having spending aligned with my values' },
  ]),
  sq('eco-07', 'Do you rely on a single paycheck/client, or do you have more than one income source?', [
    { label: 'a single source, entirely', leansEndeavour: 'Having multiple income streams' },
    { label: 'one main source plus something small', leansEndeavour: 'Having multiple income streams' },
    { label: 'several meaningful sources', leansEndeavour: 'Having multiple income streams' },
  ]),
  sq('eco-08', 'If asked "when could you stop trading time for money," do you have an actual answer?', [
    { label: "no idea, it feels impossibly far off", leansEndeavour: 'Having financial independence on a real timeline' },
    { label: 'a vague guess', leansEndeavour: 'Having financial independence on a real timeline' },
    { label: 'a specific year and number I am tracking toward', leansEndeavour: 'Having financial independence on a real timeline' },
  ]),
  sq('eco-09', 'When an unexpected expense hits (car repair, medical bill), what happens?', [
    { label: 'it goes on a card and adds to debt', leansEndeavour: 'Having a solid emergency fund' },
    { label: "it's tight but manageable", leansEndeavour: 'Having a solid emergency fund' },
    { label: 'the emergency fund covers it without stress', leansEndeavour: 'Having a solid emergency fund' },
  ]),
  sq('eco-10', 'Do you have a written or app-based budget, and do you actually check it during the month?', [
    { label: 'no budget at all', leansEndeavour: 'Having a budget I actually follow' },
    { label: 'a budget I made but rarely check', leansEndeavour: 'Having a budget I actually follow' },
    { label: 'a budget I check and adjust regularly', leansEndeavour: 'Having a budget I actually follow' },
  ]),
  sq('eco-11', 'How much of your minimum debt payment is interest versus principal right now?', [
    { label: "I don't know / mostly interest", leansEndeavour: 'Having debt paid down or eliminated' },
    { label: 'some idea', leansEndeavour: 'Having debt paid down or eliminated' },
    { label: 'I track it and it is shrinking fast', leansEndeavour: 'Having debt paid down or eliminated' },
  ]),
  sq('eco-12', 'When you got your last raise or new client, did your spending rise to match, or did the gap widen in your favor?', [
    { label: 'spending rose right along with it', leansEndeavour: 'Having spending aligned with my values' },
    { label: 'some of it went to savings', leansEndeavour: 'Having consistent, growing income' },
    { label: 'the gap widened — most of it was saved or invested', leansEndeavour: 'Having a clear investment or growth plan' },
  ]),
  sq('eco-13', 'Do you actively look for or negotiate ways to increase income (raises, side work, rate increases)?', [
    { label: 'no, I take what comes', leansEndeavour: 'Having consistent, growing income' },
    { label: 'occasionally', leansEndeavour: 'Having consistent, growing income' },
    { label: 'yes, on a regular cadence', leansEndeavour: 'Having multiple income streams' },
  ]),
  sq('eco-14', 'When you look at your last big purchase, does it reflect what you actually care about?', [
    { label: 'no, it was more about status or impulse', leansEndeavour: 'Having spending aligned with my values' },
    { label: 'somewhat', leansEndeavour: 'Having spending aligned with my values' },
    { label: 'yes, entirely', leansEndeavour: 'Having spending aligned with my values' },
  ]),
  sq('eco-15', 'Do you know your net worth (assets minus debts), even roughly?', [
    { label: 'no', leansEndeavour: 'Having a clear investment or growth plan' },
    { label: 'a rough guess', leansEndeavour: 'Having a clear investment or growth plan' },
    { label: 'yes, I track it', leansEndeavour: 'Having financial independence on a real timeline' },
  ]),
  sq('eco-16', 'If one income source disappeared, would your household finances survive?', [
    { label: 'no, it would be a crisis', leansEndeavour: 'Having multiple income streams' },
    { label: "it would hurt but we'd manage", leansEndeavour: 'Having a solid emergency fund' },
    { label: 'yes, other sources or savings would cover it', leansEndeavour: 'Having multiple income streams' },
  ]),
  sq('eco-17', 'How often do you review your subscriptions/recurring charges for things you no longer need?', [
    { label: 'never', leansEndeavour: 'Having a budget I actually follow' },
    { label: 'once in a while', leansEndeavour: 'Having a budget I actually follow' },
    { label: 'on a regular schedule', leansEndeavour: 'Having spending aligned with my values' },
  ]),
  sq('eco-18', 'Do you have a target number for financial independence (or a comfortable retirement), and a plan to reach it?', [
    { label: "never thought about a specific number", leansEndeavour: 'Having financial independence on a real timeline' },
    { label: 'a rough number in mind', leansEndeavour: 'Having financial independence on a real timeline' },
    { label: 'a specific number with a funded plan', leansEndeavour: 'Having financial independence on a real timeline' },
  ]),
  sq('eco-19', 'When debt payments are due, do they ever compete with essentials (rent, food)?', [
    { label: 'yes, regularly', leansEndeavour: 'Having debt paid down or eliminated' },
    { label: 'occasionally, in tight months', leansEndeavour: 'Having a solid emergency fund' },
    { label: 'never — they are comfortably covered', leansEndeavour: 'Having debt paid down or eliminated' },
  ]),
  sq('eco-20', 'Looking at your finances honestly, is the trajectory improving, flat, or worsening?', [
    { label: 'worsening', leansEndeavour: 'Having consistent, growing income' },
    { label: 'flat', leansEndeavour: 'Having a budget I actually follow' },
    { label: 'clearly improving, and I can show why', leansEndeavour: 'Having a clear investment or growth plan' },
  ]),
];

export const SECTOR_BANKS: Record<Sector, SectorBank> = {
  S: { sector: 'S', label: 'Social', presetEndeavours: SOCIAL_PRESETS, questions: SOCIAL_QUESTIONS },
  H: { sector: 'H', label: 'Health', presetEndeavours: HEALTH_PRESETS, questions: HEALTH_QUESTIONS },
  A: { sector: 'A', label: 'Admin', presetEndeavours: ADMIN_PRESETS, questions: ADMIN_QUESTIONS },
  P: { sector: 'P', label: 'Personal', presetEndeavours: PERSONAL_PRESETS, questions: PERSONAL_QUESTIONS },
  E: { sector: 'E', label: 'Economic', presetEndeavours: ECONOMIC_PRESETS, questions: ECONOMIC_QUESTIONS },
};

/** Sector → preset "Having …" endeavours, standalone export per spec (mirrors SECTOR_BANKS[x].presetEndeavours). */
export const PRESET_ENDEAVOURS: Record<Sector, string[]> = {
  S: SOCIAL_PRESETS,
  H: HEALTH_PRESETS,
  A: ADMIN_PRESETS,
  P: PERSONAL_PRESETS,
  E: ECONOMIC_PRESETS,
};

// ---------------------------------------------------------------------------
// Sector → deity prescription map
// ---------------------------------------------------------------------------

/** A sector deity catalog entry — the prescriptive Goal source (V9 spec §3). */
export interface SectorDeity {
  id: string;
  name: string;
  description: string;
  /** Endeavour theme keywords that suggest this deity within the sector. */
  themes: string[];
}

export const SECTOR_DEITY_MAP: Record<Sector, SectorDeity[]> = {
  S: [
    {
      id: 'sofia',
      name: 'SOFIA',
      description: 'Wisdom and relational skill — the Social sector deity. Governs listening, conflict repair, and the judgment to build and keep real connection.',
      themes: ['friends', 'family', 'dating', 'partnership', 'network', 'community', 'communication', 'conflict', 'confidence'],
    },
  ],
  H: [
    {
      id: 'hygia',
      name: 'HYGIA',
      description: 'Vitality and nutrition — the sustaining half of Health. Governs diet, energy, and the body sustained day to day.',
      themes: ['diet', 'nutrition', 'energy', 'weight', 'exercise', 'movement', 'vitality'],
    },
    {
      id: 'hypnos',
      name: 'HYPNOS',
      description: 'Sleep and recovery — the restorative half of Health. Governs rest, wind-down, and the nightly reset the body depends on.',
      themes: ['sleep', 'rest', 'recovery', 'wind-down', 'insomnia', 'fatigue'],
    },
    {
      id: 'akeso',
      name: 'AKESO',
      description: 'Healing from harm — the repair half of Health. Governs recovery from injury, illness, and chronic issues that keep recurring.',
      themes: ['injury', 'illness', 'chronic', 'healing', 'recovery from harm', 'pain', 'recurring health issue'],
    },
  ],
  A: [
    {
      id: 'hrmez',
      name: 'HRMEZ',
      description: 'Messenger and inbox order — the flow half of Admin. Governs incoming communication, mail, and the routing of tasks in.',
      themes: ['inbox', 'email', 'mail', 'paperwork', 'communication flow', 'incoming'],
    },
    {
      id: 'nemos',
      name: 'NEMOS',
      description: 'Filing and structural order — the storage half of Admin. Governs documents, digital files, and findability.',
      themes: ['documents', 'files', 'organized', 'storage', 'findable', 'accounts', 'living space'],
    },
  ],
  P: [
    {
      id: 'psyqe',
      name: 'PSYQE',
      description: 'Soul and metamorphosis — the Personal sector deity (catalog-level; tool mount arrives later). Governs identity, inner growth, and the transformation of the self over time.',
      themes: ['identity', 'growth', 'creative', 'expressive', 'emotional regulation', 'boundaries', 'reflection', 'spiritual', 'meaning'],
    },
  ],
  E: [
    {
      id: 'plutus',
      name: 'PLUTUS',
      description: 'Wealth and resource growth — the Economic sector deity. Governs income, debt, investment, and the arc toward financial independence.',
      themes: ['income', 'debt', 'savings', 'investment', 'budget', 'wealth', 'financial independence'],
    },
  ],
};

// ---------------------------------------------------------------------------
// Gear-table banks (MoyRi progression context — depths 3/10/20)
// ---------------------------------------------------------------------------

/** One answer choice on a gear-table question. */
export interface GearBankOption {
  label: string;
  /** Malady id this answer leans toward (matches `MaladySubtype.id` in spectrum-tools.ts). */
  leansMalady: string;
}

/** One gear-table question. */
export interface GearBankQuestion {
  id: string;
  prompt: string;
  options: GearBankOption[];
}

/** The full gear-table intake bank for one ORDER gear. */
export interface GearBank {
  gear: Gear;
  label: string;
  /** 20 questions; MoyRi UI slices 3 (min) / 10 (baseline) / 20 (max) per the spine spec's depth rule. */
  questions: GearBankQuestion[];
}

const gq = (id: string, prompt: string, options: GearBankOption[]): GearBankQuestion => ({ id, prompt, options });

// Gear bank questions are omitted for brevity — they're identical to web version
// Since they're not used in this port phase, we'll leave them as a stub export
export const GEAR_BANKS: Record<Gear, GearBank> = {
  orient: { gear: 'orient', label: 'Orient', questions: [] },
  regulate: { gear: 'regulate', label: 'Regulate', questions: [] },
  direct: { gear: 'direct', label: 'Direct', questions: [] },
  execute: { gear: 'execute', label: 'Execute', questions: [] },
  revise: { gear: 'revise', label: 'Revise', questions: [] },
};

// ---------------------------------------------------------------------------
// Aim-stage activity presets — the habitual activities that install a deity's
// Goal (OLMPS_SYSTEM.md STAGE 3, OLMPS_V9_SPEC.md): "the system auto-populates
// the activities habitually done to meet the aims — presets the user selects
// from (min one)." Keyed by deity id (matches SECTOR_DEITY_MAP ids).
// TODO(web): port this table to src/lib/olmps/question-banks.ts to keep the
// two platforms byte-parallel — mobile-only for now (ledger #1).
// ---------------------------------------------------------------------------

/** One recurring activity that habitually installs a deity's Goal. */
export interface ActivityPreset {
  id: string;
  label: string;
  /** Human cadence string, e.g. "3×/week · 30 min" or "daily". */
  cadence: string;
}

const ap = (id: string, label: string, cadence: string): ActivityPreset => ({ id, label, cadence });

/** deityId → 5-8 concrete recurring activities, presets the user selects from (min one). */
export const ACTIVITY_PRESETS: Record<string, ActivityPreset[]> = {
  sofia: [
    ap('sofia-1', 'Call or message one close friend just to check in', '2×/week'),
    ap('sofia-2', 'Have one uninterrupted, phone-down conversation with a loved one', '3×/week'),
    ap('sofia-3', 'Initiate plans instead of waiting to be invited', 'weekly'),
    ap('sofia-4', 'Attend a standing group, club, or community meetup', 'weekly'),
    ap('sofia-5', 'Practice naming the feeling before responding in an argument', 'as needed'),
    ap('sofia-6', 'Reach out to a professional contact to maintain the relationship', 'weekly'),
    ap('sofia-7', 'Show up fully present at a family gathering or call', 'weekly'),
    ap('sofia-8', 'Message or use an app to pursue a date', 'weekly'),
  ],
  hygia: [
    ap('hygia-1', 'Prep a home-cooked meal from real ingredients', '5×/week'),
    ap('hygia-2', 'Do a scheduled workout or movement session', '4×/week · 30 min'),
    ap('hygia-3', 'Take a brisk walk outside', 'daily · 20 min'),
    ap('hygia-4', 'Check food labels/portions before eating packaged food', 'daily'),
    ap('hygia-5', 'Batch-prep meals for the week ahead', 'weekly'),
    ap('hygia-6', 'Track energy levels across the day in a quick note', 'daily'),
    ap('hygia-7', 'Limit added sugar or alcohol for the day', 'daily'),
    ap('hygia-8', 'Do a strength-training session', '2×/week · 40 min'),
  ],
  hypnos: [
    ap('hypnos-1', 'Screens off, wind-down routine before bed', 'nightly'),
    ap('hypnos-2', 'Lights out by a fixed target time', 'nightly'),
    ap('hypnos-3', 'No caffeine after a set cutoff time', 'daily'),
    ap('hypnos-4', 'Keep the bedroom dark, cool, and phone-free', 'nightly'),
    ap('hypnos-5', 'Log hours slept and morning grogginess', 'daily'),
    ap('hypnos-6', 'Do a short stretch or breathing routine before bed', 'nightly · 10 min'),
    ap('hypnos-7', 'Hold a consistent wake time, even on weekends', 'daily'),
  ],
  akeso: [
    ap('akeso-1', 'Follow the full recovery protocol (rest, stretch, physio) after any strain', 'as needed'),
    ap('akeso-2', 'Do prescribed mobility or rehab exercises for a recurring issue', '3×/week'),
    ap('akeso-3', 'Check in on a chronic symptom and log what triggered it', 'weekly'),
    ap('akeso-4', 'Schedule and keep a follow-up appointment for an ongoing issue', 'monthly'),
    ap('akeso-5', 'Rest fully instead of pushing through when injured or sick', 'as needed'),
    ap('akeso-6', 'Apply a preventive routine (ice, stretch, posture check) for a known weak point', '3×/week'),
  ],
  hrmez: [
    ap('hrmez-1', 'Triage the inbox to zero unread', 'daily'),
    ap('hrmez-2', 'Sort incoming mail the moment it arrives (file, action, or shred)', 'daily'),
    ap('hrmez-3', 'Respond to or archive every message waiting on you', 'daily'),
    ap('hrmez-4', 'Route new admin tasks into one dedicated inbox/list, never memory', 'daily'),
    ap('hrmez-5', 'Clear voicemail and missed-call follow-ups', 'weekly'),
    ap('hrmez-6', 'Review and close out any lingering "need to reply" thread', 'weekly'),
  ],
  nemos: [
    ap('nemos-1', 'File a physical or digital document the day it arrives', 'as needed'),
    ap('nemos-2', 'Clear the desktop/downloads folder into proper folders', 'weekly'),
    ap('nemos-3', 'Update the master list of subscriptions and accounts', 'monthly'),
    ap('nemos-4', 'Tidy one area of the living space back to its home state', 'daily · 10 min'),
    ap('nemos-5', 'Back up and label key documents (ID, lease, insurance)', 'monthly'),
    ap('nemos-6', 'Review the password manager for accounts that need updating', 'monthly'),
  ],
  plutus: [
    ap('plutus-1', 'Log every expense', 'daily'),
    ap('plutus-2', 'Review the budget against actual spend', 'weekly'),
    ap('plutus-3', 'Move a fixed amount into savings or investments', 'weekly'),
    ap('plutus-4', 'Make an extra payment toward debt principal', 'monthly'),
    ap('plutus-5', 'Check net worth and update the tracking sheet', 'monthly'),
    ap('plutus-6', 'Review recurring subscriptions for ones to cancel', 'monthly'),
    ap('plutus-7', 'Research or pursue one income-growth action (raise, rate increase, side work)', 'monthly'),
  ],
  psyqe: [
    ap('psyqe-1', '10-minute journal reflection', 'daily · 10 min'),
    ap('psyqe-2', 'Sit in stillness, prayer, or meditation', 'daily · 10 min'),
    ap('psyqe-3', 'Protect a block of time that is purely mine', 'weekly'),
    ap('psyqe-4', 'Make something (write, draw, play, build) for its own sake', '3×/week'),
    ap('psyqe-5', 'Name and hold a boundary I would normally let slide', 'as needed'),
    ap('psyqe-6', 'Work through one section of a growth practice (book, course, skill)', 'weekly'),
    ap('psyqe-7', 'Ask myself what I actually want, in writing', 'weekly'),
  ],
};
